"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"

const InspirationalQuotes = () => {
  const [quotes, setQuotes] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    text: "",
    date: new Date().toISOString().split("T")[0]
  })
  const [filterFeatured, setFilterFeatured] = useState("all")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadQuotes()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadQuotes = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getQuotes()
      setQuotes(response)
    } catch (error) {
      console.error('Error loading quotes:', error)
      setError(error.message || 'Failed to load quotes')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      setError("")
      await apiService.createQuote(formData)
      setSuccessMessage('Quote created successfully!')
      setFormData({
        text: "",
        date: new Date().toISOString().split("T")[0]
      })
      setShowForm(false)
      await loadQuotes()
    } catch (error) {
      console.error('Error creating quote:', error)
      setError(error.message || 'Failed to create quote')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilterFeatured(e.target.value)
  }

  const deleteQuote = async (id) => {
    if (window.confirm("Are you sure you want to delete this quote?")) {
      try {
        setIsLoading(true)
        const quote = quotes.find(q => q.id === id)
        if (!quote) {
          console.error("Quote not found for deletion:", id)
          return
        }

        await apiService.deleteQuote(id)
        await loadQuotes()
        setSuccessMessage(`Quote deleted successfully!`)
      } catch (error) {
        console.error("Error deleting quote:", error)
        setError(error.message || "Failed to delete quote")
      } finally {
        setIsLoading(false)
      }
    }
  }

  const filteredQuotes = quotes.filter((quote) => {
    return (
      filterFeatured === "all" ||
      (filterFeatured === "featured" && quote.featured) ||
      (filterFeatured === "regular" && !quote.featured)
    )
  })

  return (
    <div className="admin-section">
      <div className="action-buttons">
        <button className="btn" onClick={() => setShowForm(!showForm)} disabled={isLoading}>
          {showForm ? "Cancel" : "Add New Quote"}
        </button>

        <div className="filter-box">
          <select value={filterFeatured} onChange={handleFilterChange} className="form-control">
            <option value="all">All Quotes</option>
            <option value="featured">Featured</option>
            <option value="regular">Regular</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Inspirational Quote</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="text">Quote</label>
              <textarea
                id="text"
                name="text"
                className="form-control"
                rows="4"
                value={formData.text}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                className="form-control"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Quote"}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="section-title">Inspirational Quotes</h2>

      <div className="quotes-list">
        {isLoading ? (
          <div className="loading">Loading quotes...</div>
        ) : filteredQuotes.length > 0 ? (
          filteredQuotes.map((quote) => (
            <div className={`quote-item ${quote.featured ? "featured" : ""}`} key={quote.id}>
              <div className="quote-content">
                <div className="quote-icon">
                  <i className="material-icons">format_quote</i>
                </div>
                <blockquote className="quote-text">{quote.text}</blockquote>
                <div className="quote-meta">
                  <p className="quote-date">{new Date(quote.date).toLocaleDateString()}</p>
                </div>
                {quote.featured && (
                  <div className="featured-badge">
                    <i className="material-icons">star</i> Featured
                  </div>
                )}
              </div>

              <div className="quote-actions">
                <button className="action-btn delete-btn" onClick={() => deleteQuote(quote.id)} disabled={isLoading}>
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-results">
            <p>No quotes found matching your filter criteria.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default InspirationalQuotes
